import React, { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';

import colors from '../../common/styles/colors';

import mlHorizontalHAV from '../../common/images/mlHorizontalHAV.png';
import mlHorizontalVAH from '../../common/images/mlHorizontalVAH.png';
import mlHorizontalHVA from '../../common/images/mlHorizontalHVA.png';
import mlVerticalHAV from '../../common/images/mlVerticalHAV.png';
import mlVerticalHVA from '../../common/images/mlVerticalHVA.png';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1400;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(15, 23, 42, 0.5);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
`;

const Card = styled.div`
  width: min(860px, calc(100vw - 48px));
  max-height: calc(100vh - 48px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 16px;
  border: 1px solid rgba(229, 232, 225, 0.9);
  background: ${colors.white};
  box-shadow:
    0 20px 60px rgba(15, 23, 42, 0.2),
    0 2px 8px rgba(15, 23, 42, 0.06);
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 22px;
  border-bottom: 1px solid ${colors.greyL};
  background: ${colors.white};
  flex-shrink: 0;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.3;
  letter-spacing: -0.01em;
  color: ${colors.black};
`;

const CloseBtn = styled.button`
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: ${colors.greyXD};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &:hover {
    background: ${colors.greyXXXL};
    color: ${colors.black};
  }

  &:focus-visible {
    outline: 2px solid ${colors.blue};
    outline-offset: 2px;
  }
`;

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px 22px 24px;
  -webkit-overflow-scrolling: touch;
`;

const Intro = styled.p`
  margin: 0 0 12px;
  font-size: 14px;
  line-height: 1.6;
  color: ${colors.greyXXD};

  &:last-of-type {
    margin-bottom: 20px;
  }
`;

const Section = styled.section`
  margin-bottom: 28px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  margin: 0 0 8px;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.35;
  color: ${colors.black};
`;

const SectionText = styled.p`
  margin: 0 0 14px;
  font-size: 13px;
  line-height: 1.55;
  color: ${colors.greyXD};
`;

const ExampleList = styled.ol`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const ExampleItem = styled.li`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ExampleLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  line-height: 1.45;
  color: ${colors.black};
`;

const Figure = styled.figure`
  margin: 0;
  padding: 12px;
  border-radius: 10px;
  border: 1px solid ${colors.greyL};
  background: ${colors.greyXXXL};
`;

const DiagramImage = styled.img`
  display: block;
  width: 100%;
  height: auto;
  max-height: 220px;
  object-fit: contain;
`;

const BulletList = styled.ul`
  margin: 0;
  padding: 0 0 0 18px;
  font-size: 13px;
  line-height: 1.6;
  color: ${colors.greyXXD};

  li {
    margin-bottom: 6px;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const DefinitionList = styled.ul`
  margin: 8px 0 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const DefinitionItem = styled.li`
  font-size: 13px;
  line-height: 1.55;
  color: ${colors.greyXXD};
`;

const DefinitionTerm = styled.strong`
  font-weight: 700;
  color: ${colors.black};
`;

const HORIZONTAL_EXAMPLES = [
  { key: 'aroundBearingsVertical', image: mlHorizontalHAV },
  { key: 'aroundBearingsHorizontal', image: mlHorizontalVAH },
  { key: 'onSidePlate', image: mlHorizontalHVA },
];

const VERTICAL_EXAMPLES = [
  { key: 'aroundBearings', image: mlVerticalHAV },
  { key: 'onSidePlate', image: mlVerticalHVA },
];

const AXIS_KEYS = ['axial', 'radial', 'tangential'];

function OrientationExamples({ namespace, examples, t }) {
  return (
    <ExampleList>
      {examples.map((example, index) => (
        <ExampleItem key={example.key}>
          <ExampleLabel>
            {String.fromCodePoint(97 + index)}. {t(`sensors.moteOrientationGuide.${namespace}.${example.key}`)}
          </ExampleLabel>
          <Figure>
            <DiagramImage
              src={example.image}
              alt={t(`sensors.moteOrientationGuide.${namespace}.${example.key}`)}
            />
          </Figure>
        </ExampleItem>
      ))}
    </ExampleList>
  );
}

OrientationExamples.propTypes = {
  namespace: PropTypes.oneOf(['horizontal', 'vertical']).isRequired,
  examples: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    image: PropTypes.string.isRequired,
  })).isRequired,
  t: PropTypes.func.isRequired,
};

const MoteOrientationGuide = ({ close }) => {
  const { t } = useTranslation();

  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Escape') {
      close();
    }
  }, [close]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  const content = (
    <Overlay role="presentation" onClick={close}>
      <Card
        role="dialog"
        aria-modal="true"
        aria-labelledby="mote-orientation-guide-title"
        onClick={event => event.stopPropagation()}
      >
        <Header>
          <Title id="mote-orientation-guide-title">
            {t('sensors.moteOrientationGuide.title')}
          </Title>
          <CloseBtn type="button" onClick={close} aria-label={t('common.close', { defaultValue: 'Close' })}>
            <FontAwesomeIcon icon={faXmark} />
          </CloseBtn>
        </Header>

        <Body>
          <Intro>{t('sensors.moteOrientationGuide.intro1')}</Intro>
          <Intro>{t('sensors.moteOrientationGuide.intro2')}</Intro>

          <Section aria-labelledby="horizontal-orientation-title">
            <SectionTitle id="horizontal-orientation-title">
              1. {t('sensors.moteOrientationGuide.horizontal.title')}
            </SectionTitle>
            <SectionText>
              {t('sensors.moteOrientationGuide.horizontal.installLocationsIntro')}
            </SectionText>
            <OrientationExamples namespace="horizontal" examples={HORIZONTAL_EXAMPLES} t={t} />
          </Section>

          <Section aria-labelledby="vertical-orientation-title">
            <SectionTitle id="vertical-orientation-title">
              2. {t('sensors.moteOrientationGuide.vertical.title')}
            </SectionTitle>
            <SectionText>
              {t('sensors.moteOrientationGuide.vertical.dischargePipeHint')}
            </SectionText>
            <SectionText>
              {t('sensors.moteOrientationGuide.vertical.installLocationsIntro')}
            </SectionText>
            <OrientationExamples namespace="vertical" examples={VERTICAL_EXAMPLES} t={t} />
          </Section>

          <Section aria-labelledby="axes-title">
            <SectionTitle id="axes-title">
              {t('sensors.moteOrientationGuide.axesIntro')}
            </SectionTitle>
            <BulletList>
              {AXIS_KEYS.map(axis => (
                <li key={axis}>{t(`sensors.moteOrientationGuide.axes.${axis}`)}</li>
              ))}
            </BulletList>
          </Section>

          <Section aria-labelledby="definitions-title">
            <SectionTitle id="definitions-title">
              {t('sensors.moteOrientationGuide.definitionsTitle')}
            </SectionTitle>
            <DefinitionList>
              {AXIS_KEYS.map(axis => (
                <DefinitionItem key={axis}>
                  <DefinitionTerm>
                    {t(`sensors.moteOrientationGuide.axisNames.${axis}`)}
                  </DefinitionTerm>
                  {' '}
                  {t(`sensors.moteOrientationGuide.definitions.${axis}`)}
                </DefinitionItem>
              ))}
            </DefinitionList>
          </Section>
        </Body>
      </Card>
    </Overlay>
  );

  if (typeof document === 'undefined') {
    return content;
  }

  return createPortal(content, document.body);
};

MoteOrientationGuide.propTypes = {
  close: PropTypes.func.isRequired,
};

export default MoteOrientationGuide;
